// To Debug: http://localhost:3000/before-after-modal-debug
import React from "react";
import {
  View,
  StyleSheet,
  TouchableWithoutFeedback,
  Modal,
  Animated,
  PanResponder,
  useWindowDimensions,
  Platform,
} from "react-native";

type TemplateSettingsModalProps = {
  visible: boolean;
  onClose: () => void;
  children: React.ReactNode;
};

type ResizeMode = "top" | "left" | "corner";
type WebResizeStartEvent = {
  preventDefault?: () => void;
  clientX?: number;
  clientY?: number;
  pageX?: number;
  pageY?: number;
};

const TemplateSettingsModal: React.FC<TemplateSettingsModalProps> = ({
  visible,
  onClose,
  children,
}) => {
  const { height: windowHeight, width: windowWidth } = useWindowDimensions();

  const baseSheetWidth = 320;
  const maxSheetWidth = Math.max(
    Math.min(windowWidth - 20, windowWidth * 0.9),
    220,
  );
  const minSheetWidth = Math.min(
    Math.max(220, baseSheetWidth * 0.9),
    maxSheetWidth - 10,
  );
  const initialSheetWidth = Math.min(
    Math.max(baseSheetWidth, minSheetWidth),
    maxSheetWidth,
  );

  const maxSheetHeight = Math.max(windowHeight - 24, 220);
  const baseSheetHeight = maxSheetHeight;
  const legacyBaseSheetHeight = Math.min(windowHeight * 0.36, 280);
  const minSheetHeight = Math.min(
    Math.max(180, legacyBaseSheetHeight * 0.6),
    maxSheetHeight - 20,
  );
  const initialSheetHeight = Math.min(
    Math.max(baseSheetHeight, minSheetHeight),
    maxSheetHeight,
  );

  const sheetWidthValue = React.useRef(
    new Animated.Value(initialSheetWidth),
  ).current;
  const currentSheetWidthRef = React.useRef(initialSheetWidth);
  const dragStartWidthRef = React.useRef(initialSheetWidth);

  const sheetHeightValue = React.useRef(
    new Animated.Value(initialSheetHeight),
  ).current;
  const currentSheetHeightRef = React.useRef(initialSheetHeight);
  const dragStartHeightRef = React.useRef(initialSheetHeight);

  const webDragCleanupRef = React.useRef<(() => void) | null>(null);
  const lastWebMouseDownAtRef = React.useRef(0);

  React.useEffect(() => {
    if (!visible) return;

    currentSheetWidthRef.current = initialSheetWidth;
    dragStartWidthRef.current = initialSheetWidth;
    sheetWidthValue.setValue(initialSheetWidth);

    currentSheetHeightRef.current = initialSheetHeight;
    dragStartHeightRef.current = initialSheetHeight;
    sheetHeightValue.setValue(initialSheetHeight);
  }, [
    visible,
    initialSheetWidth,
    initialSheetHeight,
    sheetWidthValue,
    sheetHeightValue,
  ]);

  const clampToRange = React.useCallback(
    (value: number, minValue: number, maxValue: number) =>
      Math.min(maxValue, Math.max(minValue, value)),
    [],
  );

  const applyAxisResize = React.useCallback(
    (
      delta: number,
      startValue: number,
      minValue: number,
      maxValue: number,
      currentValueRef: React.MutableRefObject<number>,
      animatedValue: Animated.Value,
    ) => {
      const nextValue = clampToRange(startValue - delta, minValue, maxValue);
      currentValueRef.current = nextValue;
      animatedValue.setValue(nextValue);
    },
    [clampToRange],
  );

  const applySheetWidth = React.useCallback(
    (dx: number, startWidth: number) => {
      applyAxisResize(
        dx,
        startWidth,
        minSheetWidth,
        maxSheetWidth,
        currentSheetWidthRef,
        sheetWidthValue,
      );
    },
    [applyAxisResize, minSheetWidth, maxSheetWidth, sheetWidthValue],
  );

  const applySheetHeight = React.useCallback(
    (dy: number, startHeight: number) => {
      applyAxisResize(
        dy,
        startHeight,
        minSheetHeight,
        maxSheetHeight,
        currentSheetHeightRef,
        sheetHeightValue,
      );
    },
    [applyAxisResize, minSheetHeight, maxSheetHeight, sheetHeightValue],
  );

  const applySheetSize = React.useCallback(
    (dx: number, dy: number, startWidth: number, startHeight: number) => {
      applySheetWidth(dx, startWidth);
      applySheetHeight(dy, startHeight);
    },
    [applySheetWidth, applySheetHeight],
  );

  const settleAxisResize = React.useCallback(
    (
      animatedValue: Animated.Value,
      currentValueRef: React.MutableRefObject<number>,
    ) => {
      Animated.spring(animatedValue, {
        toValue: currentValueRef.current,
        bounciness: 0,
        useNativeDriver: false,
      }).start();
    },
    [],
  );

  const settleSheetWidth = React.useCallback(() => {
    settleAxisResize(sheetWidthValue, currentSheetWidthRef);
  }, [settleAxisResize, sheetWidthValue]);

  const settleSheetHeight = React.useCallback(() => {
    settleAxisResize(sheetHeightValue, currentSheetHeightRef);
  }, [settleAxisResize, sheetHeightValue]);

  const settleSheetSize = React.useCallback(() => {
    settleSheetWidth();
    settleSheetHeight();
  }, [settleSheetWidth, settleSheetHeight]);

  const handleWebDoubleClickResize = React.useCallback(
    (event?: WebResizeStartEvent) => {
      if (Platform.OS !== "web" || !visible) return;

      if (typeof event?.preventDefault === "function") {
        event.preventDefault();
      }

      currentSheetWidthRef.current = clampToRange(
        currentSheetWidthRef.current * 1.5,
        minSheetWidth,
        maxSheetWidth,
      );
      currentSheetHeightRef.current = clampToRange(
        currentSheetHeightRef.current * 1.5,
        minSheetHeight,
        maxSheetHeight,
      );
      settleSheetSize();
    },
    [
      clampToRange,
      maxSheetHeight,
      maxSheetWidth,
      minSheetHeight,
      minSheetWidth,
      settleSheetSize,
      visible,
    ],
  );

  const applyResizeByMode = React.useCallback(
    (
      mode: ResizeMode,
      dx: number,
      dy: number,
      startWidth: number,
      startHeight: number,
    ) => {
      if (mode === "top") {
        applySheetHeight(dy, startHeight);
        return;
      }

      if (mode === "left") {
        applySheetWidth(dx, startWidth);
        return;
      }

      applySheetSize(dx, dy, startWidth, startHeight);
    },
    [applySheetHeight, applySheetWidth, applySheetSize],
  );

  const settleResizeByMode = React.useCallback(
    (mode: ResizeMode) => {
      if (mode === "top") {
        settleSheetHeight();
        return;
      }

      if (mode === "left") {
        settleSheetWidth();
        return;
      }

      settleSheetSize();
    },
    [settleSheetHeight, settleSheetWidth, settleSheetSize],
  );

  const handleWebDragStart = React.useCallback(
    (event: WebResizeStartEvent, mode: ResizeMode) => {
      if (Platform.OS !== "web" || !visible) return;

      const now = Date.now();
      const isDoubleClick = now - lastWebMouseDownAtRef.current < 300;
      lastWebMouseDownAtRef.current = now;
      if (isDoubleClick) {
        handleWebDoubleClickResize(event);
        return;
      }

      if (typeof event?.preventDefault === "function") {
        event.preventDefault();
      }

      const startY =
        typeof event?.clientY === "number"
          ? event.clientY
          : typeof event?.pageY === "number"
            ? event.pageY
            : 0;
      const startX =
        typeof event?.clientX === "number"
          ? event.clientX
          : typeof event?.pageX === "number"
            ? event.pageX
            : 0;

      const startHeight = currentSheetHeightRef.current;
      const startWidth = currentSheetWidthRef.current;

      const onMouseMove = (moveEvent: MouseEvent) => {
        const currentY =
          typeof moveEvent.clientY === "number"
            ? moveEvent.clientY
            : moveEvent.pageY;
        const currentX =
          typeof moveEvent.clientX === "number"
            ? moveEvent.clientX
            : moveEvent.pageX;

        applyResizeByMode(
          mode,
          currentX - startX,
          currentY - startY,
          startWidth,
          startHeight,
        );
      };

      const onMouseUp = () => {
        window.removeEventListener("mousemove", onMouseMove);
        window.removeEventListener("mouseup", onMouseUp);
        settleResizeByMode(mode);
        webDragCleanupRef.current = null;
      };

      window.addEventListener("mousemove", onMouseMove);
      window.addEventListener("mouseup", onMouseUp);

      webDragCleanupRef.current = () => {
        window.removeEventListener("mousemove", onMouseMove);
        window.removeEventListener("mouseup", onMouseUp);
      };
    },
    [
      applyResizeByMode,
      handleWebDoubleClickResize,
      settleResizeByMode,
      visible,
    ],
  );

  const topPanResponder = React.useMemo(
    () =>
      PanResponder.create({
        onStartShouldSetPanResponder: () => !!visible,
        onMoveShouldSetPanResponder: (_, gesture) =>
          !!visible &&
          Math.abs(gesture.dy) > 6 &&
          Math.abs(gesture.dy) > Math.abs(gesture.dx),
        onPanResponderGrant: () => {
          dragStartHeightRef.current = currentSheetHeightRef.current;
        },
        onPanResponderMove: (_, gesture) => {
          applySheetHeight(gesture.dy ?? 0, dragStartHeightRef.current);
        },
        onPanResponderRelease: settleSheetHeight,
        onPanResponderTerminate: settleSheetHeight,
      }),
    [applySheetHeight, settleSheetHeight, visible],
  );

  const leftPanResponder = React.useMemo(
    () =>
      PanResponder.create({
        onStartShouldSetPanResponder: () => !!visible,
        onMoveShouldSetPanResponder: (_, gesture) =>
          !!visible &&
          Math.abs(gesture.dx) > 6 &&
          Math.abs(gesture.dx) > Math.abs(gesture.dy),
        onPanResponderGrant: () => {
          dragStartWidthRef.current = currentSheetWidthRef.current;
        },
        onPanResponderMove: (_, gesture) => {
          applySheetWidth(gesture.dx ?? 0, dragStartWidthRef.current);
        },
        onPanResponderRelease: settleSheetWidth,
        onPanResponderTerminate: settleSheetWidth,
      }),
    [applySheetWidth, settleSheetWidth, visible],
  );

  const cornerPanResponder = React.useMemo(
    () =>
      PanResponder.create({
        onStartShouldSetPanResponder: () => !!visible,
        onMoveShouldSetPanResponder: (_, gesture) =>
          !!visible && (Math.abs(gesture.dx) > 4 || Math.abs(gesture.dy) > 4),
        onPanResponderGrant: () => {
          dragStartWidthRef.current = currentSheetWidthRef.current;
          dragStartHeightRef.current = currentSheetHeightRef.current;
        },
        onPanResponderMove: (_, gesture) => {
          applySheetSize(
            gesture.dx ?? 0,
            gesture.dy ?? 0,
            dragStartWidthRef.current,
            dragStartHeightRef.current,
          );
        },
        onPanResponderRelease: settleSheetSize,
        onPanResponderTerminate: settleSheetSize,
      }),
    [applySheetSize, settleSheetSize, visible],
  );

  React.useEffect(() => {
    return () => {
      if (webDragCleanupRef.current) {
        webDragCleanupRef.current();
        webDragCleanupRef.current = null;
      }
    };
  }, []);

  React.useEffect(() => {
    if (Platform.OS !== "web" || visible) return;

    if (webDragCleanupRef.current) {
      webDragCleanupRef.current();
      webDragCleanupRef.current = null;
    }
  }, [visible]);

  React.useEffect(() => {
    if (Platform.OS !== "web" || !visible) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose, visible]);

  return (
    <Modal
      visible={visible}
      transparent={true}
      animationType="slide"
      onRequestClose={onClose}
    >
      <TouchableWithoutFeedback onPress={onClose}>
        <View style={styles.modalContainer}>
          <TouchableWithoutFeedback>
            <Animated.View
              style={[
                styles.modalContent,
                {
                  width: sheetWidthValue,
                  maxWidth: maxSheetWidth,
                  minWidth: minSheetWidth,
                  height: sheetHeightValue,
                  maxHeight: maxSheetHeight,
                  minHeight: minSheetHeight,
                },
              ]}
            >
              <View
                style={[
                  styles.leftDragRegion,
                  Platform.OS === "web"
                    ? ({ cursor: "ew-resize", userSelect: "none" } as any)
                    : null,
                ]}
                {...leftPanResponder.panHandlers}
                {...(Platform.OS === "web"
                  ? ({
                      onMouseDown: (event: WebResizeStartEvent) =>
                        handleWebDragStart(event, "left"),
                    } as any)
                  : {})}
              >
                <View pointerEvents="none" style={styles.leftHandleZone}>
                  <View style={[styles.modalHandle, styles.leftHandle]} />
                </View>
              </View>
              <View
                style={[
                  styles.modalDragRegion,
                  Platform.OS === "web"
                    ? ({ cursor: "ns-resize", userSelect: "none" } as any)
                    : null,
                ]}
                {...topPanResponder.panHandlers}
                {...(Platform.OS === "web"
                  ? ({
                      onMouseDown: (event: WebResizeStartEvent) =>
                        handleWebDragStart(event, "top"),
                    } as any)
                  : {})}
              >
                <View style={styles.modalHandleZone}>
                  <View style={styles.modalHandle} />
                </View>
              </View>
              <View
                style={[
                  styles.cornerDragRegion,
                  Platform.OS === "web"
                    ? ({ cursor: "nwse-resize", userSelect: "none" } as any)
                    : null,
                ]}
                {...cornerPanResponder.panHandlers}
                {...(Platform.OS === "web"
                  ? ({
                      onMouseDown: (event: WebResizeStartEvent) =>
                        handleWebDragStart(event, "corner"),
                    } as any)
                  : {})}
              />
              {children}
            </Animated.View>
          </TouchableWithoutFeedback>
        </View>
      </TouchableWithoutFeedback>
    </Modal>
  );
};

const styles = StyleSheet.create({
  modalContent: {
    position: "relative",
    paddingHorizontal: 12,
    paddingTop: 8,
    paddingBottom: 10,
    borderWidth: 1,
    borderColor: "#DDDEE0",
    backgroundColor: "rgba(248, 248, 248, 0.85)",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    alignItems: "center",
  },
  modalContainer: {
    flex: 1,
    justifyContent: "flex-end",
    alignItems: "flex-end",
    paddingRight: 10,
    paddingBottom: 8,
  },
  modalHandle: {
    width: 32,
    height: 3,
    borderRadius: 2,
    backgroundColor: "#C4C6CD",
  },
  modalDragRegion: {
    width: "100%",
    minHeight: 48,
    justifyContent: "center",
    alignItems: "center",
    marginTop: -8,
  },
  modalHandleZone: {
    width: "100%",
    minHeight: 40,
    justifyContent: "center",
    alignItems: "center",
  },
  leftDragRegion: {
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    width: 14,
    zIndex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  leftHandleZone: {
    width: 20,
    height: 44,
    justifyContent: "center",
    alignItems: "center",
  },
  leftHandle: {
    transform: [{ rotate: "90deg" }],
  },
  cornerDragRegion: {
    position: "absolute",
    left: 0,
    top: 0,
    width: 20,
    height: 20,
    zIndex: 2,
  },
});

export default TemplateSettingsModal;
